import { validationProblemsTag } from '../../apis/apiValidationProblems';
import {
  apiConfigurationTags,
  apiSlice,
  ErrorLike,
  errorTransform,
  TemplateOrCalculation,
} from '../../apis/api';
import { CalculationsCollection, TemplatesCollection } from '../../swagger/collections';
import {
  ApiAction,
  ApiOperationType,
  CalculationStatusRequest,
  CalculationStatusResponse,
  CalculationWithOverviewResponse,
  CategoryFieldConfigurationResponse,
  ConfigurationFieldType,
  FieldConfigurationRequest,
  SkuType,
  TemplateRequest,
  TemplateResponse,
  TemplateUpdateRequest,
  ValidationArea,
} from '@ydistri/api-sdk';
import { SignalConfigurationChanged } from '../../signalr/signalrInterfaces';
import { createDebugLog, EntityListConfiguration } from '@ydistri/utils';
import { signalrClient } from '../../signalr/client';
import { apiApplication } from '../../apis/apiApplication';
import { getSelectedProjectShortName } from '../../apis/apiHeader';
import { getTags } from '../../apis/apiLib';
import {
  entityListScopeExcludedConfigurationPairs,
  isScopeConfigurationWithExcludedVariant,
} from './ConfigurationCategories/SegmentPotentialDefinition/ConfigurationEntityLists/entityListLib';
import { ROOT_CATEGORY_ID } from '../../components/global/_constants';
import { BaseTemplatePayload } from '../../lib/templates/templateTypes';
import { capitalizeFirstLetter } from '@ydistri/generic-utils';

const debugLog = createDebugLog('apiTemplate');

const { TAGS, TAGS_ARRAY } = getTags('template');

export interface PatchCategoryConfigurationPayload extends BaseTemplatePayload {
  categoryId: number;
  payload: FieldConfigurationRequest[];
}

export interface PatchTemplatePayload extends BaseTemplatePayload {
  data: TemplateUpdateRequest;
}

/**
 * Used to recompute the template
 */
export interface PatchTemplateStatusPayload extends BaseTemplatePayload {
  data: CalculationStatusRequest;
}

export const apiTemplate = apiSlice.enhanceEndpoints({ addTagTypes: TAGS_ARRAY }).injectEndpoints({
  endpoints: builder => ({
    patchTemplateStatus: builder.mutation<CalculationStatusResponse, PatchTemplateStatusPayload>({
      queryFn: async ({ templateId, data: payload }) => {
        const { data } = await TemplatesCollection.patchTemplateConfiguration(templateId, payload);
        return data;
      },
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response from mutation
    patchCategoryConfiguration: builder.mutation<void, PatchCategoryConfigurationPayload>({
      invalidatesTags: (result, error, { templateId }) => [
        validationProblemsTag({ type: 'Template', id: templateId }, ValidationArea.Categories),
      ],
      queryFn: async ({ templateId, categoryId, payload }) => {
        const { data } = await TemplatesCollection.patchTemplateCategoryConfiguration(
          templateId,
          categoryId,
          payload,
        );

        return { data };
      },
    }),

    getRootCategoryConfiguration: builder.query<
      CategoryFieldConfigurationResponse[],
      TemplateOrCalculation
    >({
      queryFn: async arg => {
        debugLog('TEST - Calling getRootCategoryConfiguration', arg.id);
        const { data: rootConfigurationData } = await (arg.type === 'Template'
          ? TemplatesCollection.getTemplateCategoryConfigurations(arg.id, ROOT_CATEGORY_ID)
          : CalculationsCollection.getCalculationCategoryConfigurations(arg.id, ROOT_CATEGORY_ID));

        return { data: rootConfigurationData.data };
      },
      providesTags: (result, error, arg) => [
        { type: TAGS.categoryConfiguration, id: `${arg.type}-${arg.id}` },
      ],
      async onCacheEntryAdded({ id, type }, api) {
        try {
          await api.cacheDataLoaded;

          /**
           * When adding Calculation to cache, we do not need to attach SignalR listener
           * because the configuration will never change
           */
          if (type === 'Calculation') return;

          const listener = (signal: SignalConfigurationChanged) => {
            if (id !== signal.templateId) {
              // update only cache entry for the template that emitted this signal
              return;
            }

            if (
              // When Loading configuration into template, we reset all cached data for this template
              signal.apiAction === ApiAction.LoadConfigurationsFromCalculation &&
              signal.operationType === ApiOperationType.Replace
            ) {
              debugLog('Load into template...');
              api.dispatch(apiSlice.util.invalidateTags(apiConfigurationTags));
            } else if (signal.entityType === 'Category') {
              api.updateCachedData(draft => {
                const { updates, entityId: categoryId, operationType } = signal;
                // eslint-disable-next-line no-console -- debug
                console.log('SIGNAL [in RootCategoryConfiguration]', signal);
                if (categoryId !== ROOT_CATEGORY_ID) return;
                if (operationType === ApiOperationType.Patch) {
                  updates.forEach(u => {
                    if (u.value !== undefined && u.value !== null) {
                      const split = u.fieldName.split('|').map(capitalizeFirstLetter);
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know that split[0] is ConfigurationFieldType
                      const fieldType = split[0] as ConfigurationFieldType;
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know that split[1] is SkuType
                      const skuType = (split[1] === 'Null' ? null : split[1]) as SkuType;

                      const foundException = draft.find(
                        e => e.fieldType === fieldType && e.skuType === skuType,
                      );
                      if (foundException) {
                        foundException.value = u.value;
                        // eslint-disable-next-line no-console -- debug
                        console.log('exception found', { foundException }, 'setting to ', u.value);
                      } else {
                        // eslint-disable-next-line no-console -- debug
                        console.log('Exception not found', draft);
                      }
                    } else {
                      throw Error(`No value in update!`);
                    }
                  });
                }
              });
            } else if (signal.entityType === 'EntityListConfiguration') {
              const { entityId: entityListId } = signal;
              // eslint-disable-next-line no-console -- debug
              console.log('SIGNAL EntityListConfiguration', signal);
              if (signal.operationType === ApiOperationType.Create) return;
              if (signal.operationType === ApiOperationType.Delete) {
                api.updateCachedData(draft => {
                  draft.forEach(conf => {
                    signal.updates.forEach(u => {
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- needed
                      const uFieldName = capitalizeFirstLetter(
                        u.fieldName,
                      ) as ConfigurationFieldType;
                      if (
                        conf.fieldType === uFieldName ||
                        (isScopeConfigurationWithExcludedVariant(uFieldName) &&
                          conf.fieldType === entityListScopeExcludedConfigurationPairs[uFieldName])
                      ) {
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know parse() results to EntityListConfiguration[]
                        const oldConfigs = JSON.parse(conf.value) as EntityListConfiguration[];
                        if (oldConfigs.length > 0) {
                          const newConfigs = oldConfigs.filter(
                            x => x.EntityListId !== entityListId,
                          );
                          if (newConfigs.length !== oldConfigs.length) {
                            conf.value = JSON.stringify(newConfigs);
                          }
                        }
                      }
                    });
                  });
                });
              }
            }
          };

          debugLog('TEST - [signalrClient.on] TemplateId: ', id);
          signalrClient.on('TemplateConfigurationChanged', listener);

          api.cacheEntryRemoved.then(() => {
            debugLog('[signalrClient.off] Cache entry removed! TemplateId: ', id);
            signalrClient.off('TemplateConfigurationChanged', listener);
          });
        } catch {
          // no-op in case cache entry was removed before data was loaded
        }
        await api.cacheEntryRemoved;
      },
    }),
    getTemplate: builder.query<TemplateResponse | undefined, number | undefined>({
      queryFn: async templateId => {
        if (templateId) {
          const response = await TemplatesCollection.getTemplate(templateId);
          return { data: response.data.data };
        } else {
          return { data: undefined };
        }
      },
      providesTags: result => {
        if (!result) return [];
        return [{ type: TAGS.template, id: result.id }];
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no request params
    getOverlappingCalculations: builder.query<CalculationWithOverviewResponse[], void>({
      queryFn: async () => {
        const response = await TemplatesCollection.getAllOverlappingCalculations();
        return { data: response.data.data.map(c => ({ ...c })) };
      },
      providesTags: result => {
        if (!result) return [];
        return [{ type: TAGS.overlappingCalculations }];
      },
    }),
    patchTemplate: builder.mutation<TemplateResponse, PatchTemplatePayload>({
      queryFn: async ({ templateId, data }) => {
        try {
          const response = await TemplatesCollection.patchTemplate(templateId, data);
          return { data: response.data.data };
        } catch (error) {
          // Returned, not thrown: a thrown error reaches the caller as its message only, and the
          // modal reads the server's problems off response.data.
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      onQueryStarted(payload, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          apiApplication.util.updateQueryData(
            'getTemplates',
            getSelectedProjectShortName() ?? '',
            draft => {
              const existingTemplate = draft.find(template => template.id === payload.templateId);
              if (existingTemplate) {
                Object.assign(existingTemplate, payload.data);
              }
            },
          ),
        );
        queryFulfilled.catch(patchResult.undo);
      },
    }),

    createTemplate: builder.mutation<TemplateResponse, TemplateRequest>({
      queryFn: async data => {
        try {
          const response = await TemplatesCollection.postTemplate(data);
          return { data: response.data.data };
        } catch (error) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- generated SDK error
          return { error: errorTransform(error as ErrorLike) };
        }
      },
      async onQueryStarted(payload, { dispatch, queryFulfilled }) {
        // The template belongs to the project selected now, even if the user switches meanwhile
        const projectShortName = getSelectedProjectShortName() ?? '';
        try {
          const { data } = await queryFulfilled;
          dispatch(
            apiApplication.util.updateQueryData('getTemplates', projectShortName, draft => {
              // The list's TemplateStatusChanged listener can add the template before the answer arrives
              if (!draft.some(template => template.id === data.id)) draft.push(data);
            }),
          );
        } catch {
          // A failed or aborted request adds nothing
        }
      },
    }),

    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no response here
    deleteTemplate: builder.mutation<void, number>({
      queryFn: async templateId => {
        const response = await TemplatesCollection.deleteTemplate(templateId);
        return { data: response.data };
      },
      async onQueryStarted(templateId, { dispatch, queryFulfilled }) {
        // The template belongs to the project selected now, even if the user switches meanwhile
        const projectShortName = getSelectedProjectShortName() ?? '';
        try {
          await queryFulfilled;
          dispatch(
            apiApplication.util.updateQueryData('getTemplates', projectShortName, draft =>
              draft.filter(template => template.id !== templateId),
            ),
          );
        } catch {
          // A failed or aborted request removes nothing
        }
      },
    }),
  }),
});

export const {
  usePatchTemplateStatusMutation,
  usePatchCategoryConfigurationMutation,
  useGetRootCategoryConfigurationQuery,
  useGetTemplateQuery,
  useGetOverlappingCalculationsQuery,
  usePatchTemplateMutation,
  useCreateTemplateMutation,
  useDeleteTemplateMutation,
} = apiTemplate;
